import React, { useEffect, useMemo, useRef, useState } from 'react';
import AskIcon from 'shared/components/ui/AskIcon';
import { createPortal } from 'react-dom';
import { Search, X, Plus, ChevronDown, PenLine } from 'lucide-react';
import { listTemplates } from 'features/evals3/api/evals3Api';
import { kindMeta } from 'features/evals3/editor/nodeKinds';
import { USE_KIND } from 'features/evals3/templates';

/**
 * "New eval": start from a template, from scratch, or by describing it to Lucid.
 *
 * The templates are the server's catalog (GET /eval3/templates) — the common
 * evals, each a complete eval to adapt. Search reads a template's name,
 * description, when-to-use line and the kinds of step it uses; the category
 * rail narrows it. Picking one opens the create page filled in from it;
 * nothing is saved until the person creates it.
 */

/** The node kind a catalog `uses` key stands for, as the editor draws it. */
const stepMeta = (u) => kindMeta(USE_KIND[u] || u);

/** The three to reach for first: one of each way of deciding. */
const START_HERE = ['rag-faithfulness', 'answer-relevance', 'tool-call-succeeded'];

/* The dialog rises in. Off under prefers-reduced-motion: the point is a
   sense of the panel arriving, never a wait. */
const MOTION = `
.eval-template-dialog { animation: evalDialogIn 160ms cubic-bezier(0.16, 1, 0.3, 1) both; }
@keyframes evalDialogIn { from { opacity: 0; transform: translateY(-8px) scale(0.985); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .eval-template-dialog { animation: none; } }`;

const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
// The same "+ Add ▾" button and menu a dataset uses, so a create-menu looks one way.
const createBtn = `inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-ink-onBrand transition-colors hover:opacity-90 disabled:opacity-50 ${focusRing}`;
const menuClass = 'absolute right-0 top-[calc(100%+4px)] z-30 w-56 rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-1 shadow-token-lg';
const itemClass = `flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[12px] text-[color:var(--text-primary)] hover:bg-surface-hover focus-visible:bg-surface-hover ${focusRing}`;

/**
 * A small menu that closes on outside click or Escape. Escape stops here: the
 * dialog listens on window, so the first Escape closes only the menu.
 */
function useMenu() {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  const trigger = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    // Keyboard users land on the first option; a mouse click shows no ring.
    box.current?.querySelector('[role="menuitem"]')?.focus();
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return { open, setOpen, box, trigger };
}

/** Up and down move between the options. */
function onMenuArrows(e) {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
  e.preventDefault();
  const items = [...e.currentTarget.querySelectorAll('[role="menuitem"]')];
  const at = items.indexOf(document.activeElement);
  const next = e.key === 'ArrowDown' ? (at + 1) % items.length : (at - 1 + items.length) % items.length;
  items[next]?.focus();
}

/**
 * The kinds of step a template uses: one small mark each, in the colour the
 * step wears on the canvas, named on hover and to a screen reader.
 */
function UseMarks({ uses }) {
  if (!uses?.length) return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-[color:var(--text-muted)]">
      {uses.map((u) => {
        const { Icon, label, accent } = stepMeta(u);
        return (
          <span key={u} role="img" aria-label={label} title={label} className="inline-flex" style={accent ? { color: accent } : undefined}>
            <Icon size={14} aria-hidden />
          </span>
        );
      })}
    </span>
  );
}

export function matchesTemplate(t, q) {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  const hay = [t.name, t.description, t.when, t.category, t.target, ...(t.uses || []).map((u) => stepMeta(u).label)].join(' ').toLowerCase();
  return s.split(/\s+/).every((w) => hay.includes(w));
}

export default function NewEvalDialog({ teamId, open, onClose, onPick, onScratch, onAsk }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const inputRef = useRef(null);
  const menu = useMenu();
  const { setOpen: setMenuOpen } = menu;

  useEffect(() => {
    if (!open || data) return undefined;
    let off = false;
    Promise.resolve(listTemplates(teamId))
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off) setError(e?.response?.data?.error || e.message); });
    return () => { off = true; };
  }, [open, teamId, data]);

  useEffect(() => {
    if (!open) return undefined;
    setQ(''); setCat('all'); setMenuOpen(false);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open, onClose, setMenuOpen]);

  // Memoised, not defaulted inline: `|| []` is a fresh array every render, and
  // every list below it would recompute on every keystroke.
  const categories = useMemo(() => data?.categories || [], [data]);
  const all = useMemo(() => data?.templates || [], [data]);
  const shown = useMemo(() => all.filter((t) => (cat === 'all' || t.category === cat) && matchesTemplate(t, q)), [all, cat, q]);
  const grouped = useMemo(() => categories
    .map((c) => ({ ...c, items: shown.filter((t) => t.category === c.id) }))
    .filter((g) => g.items.length), [categories, shown]);
  const counts = useMemo(() => {
    const m = { all: all.length };
    for (const t of all) m[t.category] = (m[t.category] || 0) + 1;
    return m;
  }, [all]);
  // Three to reach for first, one per way of deciding. Only when nothing is
  // being searched or narrowed: once someone is looking for something, the
  // thing they are looking for is the point, not our suggestions.
  const starters = useMemo(() => (q.trim() || cat !== 'all'
    ? []
    : START_HERE.map((id) => all.find((t) => t.id === id)).filter(Boolean)), [all, q, cat]);

  if (!open) return null;

  const el = (
    <div className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[7vh]"
      style={{ background: 'color-mix(in srgb, var(--text-primary) 40%, transparent)' }}
      role="dialog" aria-modal="true" aria-labelledby="new-eval-title"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{MOTION}</style>
      <div className="eval-template-dialog flex max-h-[84vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-2xl"
        style={{ fontFamily: 'Inter, sans-serif' }}>
        <div className="flex items-center gap-3 border-b border-[color:var(--border-subtle)] px-4 py-3">
          <h2 id="new-eval-title" className="shrink-0 text-[14px] font-semibold text-[color:var(--text-primary)]">New eval</h2>
          {/* Searches everything, not the category in the rail. */}
          <div className="flex h-8 min-w-0 max-w-md flex-1 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-3 focus-within:border-[color:var(--border-strong)]">
            <Search size={14} className="shrink-0 text-[color:var(--text-muted)]" aria-hidden />
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…"
              aria-label="Search templates" className="min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-disabled)]" />
            {q && <button type="button" onClick={() => setQ('')} aria-label="Clear search" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><X size={13} /></button>}
          </div>
          <div className="flex-1" />
          <div ref={menu.box} className="relative">
            <button ref={menu.trigger} type="button" onClick={() => menu.setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={menu.open} className={createBtn}>
              <Plus size={13} /> Create eval <ChevronDown className="h-3.5 w-3.5" />
            </button>
            {menu.open ? (
              <div role="menu" aria-label="Create eval" className={menuClass} onKeyDown={onMenuArrows}>
                <button type="button" role="menuitem" onClick={() => { menu.setOpen(false); onAsk(); }} className={itemClass}>
                  <AskIcon size={13} className="text-[color:var(--brand-primary)]" /> Create with Lucid
                </button>
                <button type="button" role="menuitem" onClick={() => { menu.setOpen(false); onScratch(); }} className={itemClass}>
                  <PenLine size={13} className="text-[color:var(--text-muted)]" aria-hidden /> Start from scratch
                </button>
              </div>
            ) : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={`rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] ${focusRing}`}><X size={16} /></button>
        </div>

        <div className="flex min-h-0 flex-1">
          {/* Browse rail: every category with how many it holds, so the shape of
              the catalog is visible before anything is clicked. */}
          <nav className="hidden w-[186px] shrink-0 flex-col overflow-y-auto border-r border-[color:var(--border-subtle)] px-2 py-2.5 sm:flex" aria-label="Categories">
            <div className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--text-muted)]">Browse</div>
            <div role="radiogroup" aria-label="Category" className="flex flex-col gap-0.5">
              {[{ id: 'all', label: 'All' }, ...categories].map((c) => {
                const on = cat === c.id;
                return (
                  <button key={c.id} type="button" role="radio" aria-checked={on} onClick={() => setCat(c.id)}
                    className={`flex h-8 items-center gap-2 rounded-md px-2 text-left text-[12px] transition-colors ${focusRing} ${on
                      ? 'bg-surface-selected font-medium text-[color:var(--text-primary)]'
                      : 'text-[color:var(--text-secondary)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]'}`}>
                    <span className="min-w-0 flex-1 truncate">{c.label}</span>
                    <span className="ml-auto shrink-0 text-right text-[11px] tabular-nums text-[color:var(--text-muted)]">{counts[c.id] || 0}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3" data-testid="template-list">
            {error && <div className="text-[12px] text-danger" role="alert">Templates could not load: {error}</div>}
            {!data && !error && <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">Loading templates…</div>}
            {data && shown.length === 0 && (
              <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">
                No template matches “{q}”. <button type="button" onClick={onScratch} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">Start from scratch</button> or <button type="button" onClick={onAsk} className="font-medium text-[color:var(--text-primary)] underline-offset-2 hover:underline">describe it to Lucid</button>.
              </div>
            )}

            {starters.length > 0 && (
              <section className="mb-5" aria-label="Start here">
                <h3 className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Start here</h3>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {starters.map((t) => (
                    <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`starter-${t.id}`}
                      className={`flex flex-col items-start gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface p-3 text-left transition-colors hover:border-[color:var(--border-strong)] hover:bg-surface-hover ${focusRing}`}>
                      <span className="flex w-full items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[color:var(--text-primary)]">{t.name}</span>
                        <UseMarks uses={t.uses} />
                      </span>
                      <span className="line-clamp-2 text-[12px] leading-snug text-[color:var(--text-muted)]">{t.description}</span>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {grouped.map((g) => (
              <section key={g.id} className="mb-4" aria-label={g.label}>
                <h3 className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">{g.label}</h3>
                <div className="flex flex-col">
                  {g.items.map((t) => (
                    <button key={t.id} type="button" onClick={() => onPick(t)} data-testid={`template-${t.id}`}
                      className={`-mx-1.5 flex h-9 items-center gap-3 rounded-md px-1.5 text-left transition-colors hover:bg-surface-hover ${focusRing}`}>
                      <span className="w-[210px] shrink-0 truncate text-[13px] font-medium text-[color:var(--text-primary)]">{t.name}</span>
                      <span className="min-w-0 flex-1 truncate text-[12px] text-[color:var(--text-muted)]">{t.description}</span>
                      <UseMarks uses={t.uses} />
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
  return createPortal(el, document.body);
}
